// Code 128 (ISO/IEC 15417) encoder for the order ID printed in the sheet footer, so that a scanner in the
// print shop can read it. Uses subsets B (printable ASCII) and C (digit pairs, which halves the width of a
// run of digits like the timestamp in "gen-1789909069888"). Pure and free of DOM/React, so it is easy to test.

// Bar/space widths of every symbol, starting with a bar: values 0–102 are the characters, 103–105 the start
// codes A/B/C and the last entry is the stop pattern (7 elements, 13 modules).
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '2331112',
];

const START_B = 104;
const START_C = 105;
const CODE_B = 100;   // switch C → B
const CODE_C = 99;    // switch B → C
const STOP = 106;

const isDigit = (c: string) => c >= '0' && c <= '9';

// A run of digits is worth switching to subset C for when it is long enough to pay for the switch:
// 4+ at the start or the end of the text, 6+ in the middle, and 2+ when the whole text is digits.
function useSubsetC(runLength: number, atStart: boolean, atEnd: boolean): boolean {
  if (atStart && atEnd) return runLength >= 2;
  if (atStart || atEnd) return runLength >= 4;
  return runLength >= 6;
}

/** Symbol values from the start code to the check symbol (without the stop), or null if `text` cannot be encoded. */
export function code128Values(text: string): number[] | null {
  if (!text) return null;
  const n = text.length;
  const values: number[] = [];
  let subset: 'B' | 'C' | null = null;

  const enterB = () => {
    if (subset === null) values.push(START_B);
    else if (subset === 'C') values.push(CODE_B);
    subset = 'B';
  };
  const enterC = () => {
    if (subset === null) values.push(START_C);
    else if (subset === 'B') values.push(CODE_C);
    subset = 'C';
  };
  const pushB = (ch: string) => {
    const code = ch.charCodeAt(0);
    if (code < 32 || code > 126) return false;
    enterB();
    values.push(code - 32);
    return true;
  };

  let i = 0;
  while (i < n) {
    let run = 0;
    while (i + run < n && isDigit(text[i + run])) run++;
    if (run > 0 && useSubsetC(run, i === 0, i + run === n)) {
      // Subset C holds digits in pairs: with an odd run the first digit goes in subset B.
      if (run % 2 === 1) {
        pushB(text[i]);
        i++;
        run--;
      }
      enterC();
      for (let k = 0; k < run; k += 2) values.push(parseInt(text.slice(i + k, i + k + 2), 10));
      i += run;
    } else {
      if (!pushB(text[i])) return null;
      i++;
    }
  }

  let checksum = values[0];
  for (let k = 1; k < values.length; k++) checksum += values[k] * k;
  values.push(checksum % 103);
  return values;
}

/** Widths of the alternating bars and spaces of the whole symbol (first element is a bar), or null if it cannot be encoded. */
export function code128Widths(text: string): number[] | null {
  const values = code128Values(text);
  if (!values) return null;
  return [...values, STOP].flatMap(value => PATTERNS[value].split('').map(Number));
}

/** Width of the symbol in modules (without quiet zones), or 0 if `text` cannot be encoded. */
export function code128ModuleCount(text: string): number {
  return code128Widths(text)?.reduce((sum, w) => sum + w, 0) ?? 0;
}
